import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { awardedItems as mockAwardedItems } from "@/mocks/awardedItems";

/**
 * 낙찰물건 인사이트 — kangstudy_awarded_items 를 읽는다(관리자 엑셀 편집기로 입력).
 *
 * 목업(mocks/awardedItems.ts)에는 있지만 실제 테이블엔 아직 없는 컬럼이 있다
 * (keyLessons·profitBreakdown·regionalContext·similarCurrentListings — 전문가가
 * 손으로 쓰는 심층 콘텐츠라 스프레드시트 편집기 범위 밖). 실데이터 항목은 이
 * 필드들을 null 로 두고, 화면에서 있을 때만 그 섹션을 그린다 — 없는 근거를
 * 지어내지 않는다.
 *
 * DB 는 snake_case, 화면은 camelCase. 조회 실패/빈 테이블은 목업으로 폴백하되
 * `usingMock` 을 같이 돌려준다(폴백을 조용히 하면 알아챌 수 없다).
 */
export interface AwardedProfitBreakdown {
  winningBid: number;
  acquisitionTax: number;
  evictionCost: number;
  renovationCost: number;
  brokerageFee: number;
  totalCost: number;
  estimatedMarketPrice: number;
  estimatedProfit: number;
  profitRate: number;
}

export interface AwardedSimilarListing {
  location: string;
  minimumPrice: number;
  auctionDate: string;
}

export interface AwardedItemView {
  id: string;
  caseNumber: string;
  court: string;
  location: string;
  propertyType: string;
  appraisedValue: number;
  minimumPrice: number;
  auctionDate: string;
  failureCount: number;
  exclusiveArea: number;
  buildingAge: number;
  actualWinningBid: number;
  bidderCount: number;
  bidCompetitionRate: string;
  expertGrade: "적극추천" | "추천" | "관망";
  expertSummary: string;
  expertFullAnalysis: string;
  region: string;
  imageUrl: string;
  keyLessons: string[] | null;
  profitBreakdown: AwardedProfitBreakdown | null;
  regionalContext: string | null;
  similarCurrentListings: AwardedSimilarListing[] | null;
}

function toView(r: Record<string, unknown>): AwardedItemView {
  const num = (v: unknown): number => (v === null || v === undefined || v === "" ? 0 : Number(v));
  return {
    id: String(r.id),
    caseNumber: (r.case_number as string) || "",
    court: (r.court as string) || "",
    location: (r.location as string) || "",
    propertyType: (r.property_type as string) || "",
    appraisedValue: num(r.appraised_value),
    minimumPrice: num(r.minimum_price),
    auctionDate: (r.auction_date as string) || "",
    failureCount: num(r.failure_count),
    exclusiveArea: num(r.exclusive_area),
    buildingAge: num(r.building_age),
    actualWinningBid: num(r.actual_winning_bid),
    bidderCount: num(r.bidder_count),
    bidCompetitionRate: (r.bid_competition_rate as string) || "",
    expertGrade: ((r.expert_grade as string) || "관망") as AwardedItemView["expertGrade"],
    expertSummary: (r.expert_summary as string) || "",
    expertFullAnalysis: (r.expert_full_analysis as string) || "",
    region: (r.region as string) || "",
    imageUrl: (r.image_url as string) || "",
    // 실데이터 테이블엔 아직 없는 컬럼 — 지어내지 않고 null.
    keyLessons: null,
    profitBreakdown: null,
    regionalContext: null,
    similarCurrentListings: null,
  };
}

function mockToView(m: (typeof mockAwardedItems)[number]): AwardedItemView {
  return {
    id: m.id,
    caseNumber: m.caseNumber,
    court: m.court,
    location: m.location,
    propertyType: m.propertyType,
    appraisedValue: m.appraisedValue,
    minimumPrice: m.minimumPrice,
    auctionDate: m.auctionDate,
    failureCount: m.failureCount,
    exclusiveArea: m.exclusiveArea,
    buildingAge: m.buildingAge,
    actualWinningBid: m.actualWinningBid,
    bidderCount: m.bidderCount,
    bidCompetitionRate: m.bidCompetitionRate,
    expertGrade: m.expertGrade,
    expertSummary: m.expertSummary,
    expertFullAnalysis: m.expertFullAnalysis,
    region: m.region,
    imageUrl: m.imageUrl,
    keyLessons: m.keyLessons,
    profitBreakdown: m.profitBreakdown,
    regionalContext: m.regionalContext,
    similarCurrentListings: m.similarCurrentListings,
  };
}

export function useAwardedItems() {
  const [items, setItems] = useState<AwardedItemView[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("kangstudy_awarded_items")
        .select("*")
        .order("auction_date", { ascending: false });

      if (error) throw error;
      if (data && data.length > 0) {
        setItems(data.map((r) => toView(r as Record<string, unknown>)));
        setUsingMock(false);
      } else {
        setItems(mockAwardedItems.map(mockToView));
        setUsingMock(true);
      }
    } catch (e) {
      console.error("[kangstudy] 낙찰물건 인사이트 조회 실패 — 목업으로 폴백합니다:", e);
      setItems(mockAwardedItems.map(mockToView));
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, loading, usingMock, refetch: fetchItems };
}
